/**
 * The logo library, from screen-library in docs/prototype.html.
 *
 * Every company with approved artwork for this client, reusable year over
 * year. This is the payoff for the model's central decision — a sponsor
 * COMPANY persists while a SPONSORSHIP belongs to one event, so artwork
 * outlives the conference it arrived for.
 *
 * ONLY APPROVED ARTWORK IS HERE. The library means "safe to use": if it is on
 * this screen it can be handed to a designer without checking. Pending and
 * rejected uploads are deliberately absent, because a library you have to read
 * carefully is not doing its job.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { T } from '../lib/copyEdit.tsx';
import { t } from '../lib/copy';

interface LibraryItem {
  sponsorId: string;
  companyName: string;
  website: string | null;
  appIconData: string | null;
  tierTitle: string | null;
  latestEvent: string;
  latestAt: string;
  /** The asset id to render, or null when nothing this company has can be
   *  drawn at all. Since derivatives exist that now means EPS, a failed
   *  render, or an asset the backfill has not reached. */
  preview: string | null;
  /** Which generated copies the cover has, so the card can ask for a thumb
   *  rather than pulling a full-size original into a 120px tile. */
  previewRenditions: string[];
  variationCount: number;
  eventCount: number;
}

interface FilterEvent {
  id: string;
  name: string;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export default function Library() {
  const { clientId } = useParams();
  const base = `/api/clients/${clientId}/library`;

  const [items, setItems] = useState<LibraryItem[] | null>(null);
  const [events, setEvents] = useState<FilterEvent[]>([]);
  const [search, setSearch] = useState('');
  /** Empty for every event. The prototype's pill said Year; event is the
   *  better axis, for the same reason the version history groups by one. */
  const [eventId, setEventId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (q: string, event: string) => {
      try {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (event) params.set('eventId', event);
        const res = await fetch(`${base}?${params.toString()}`);
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { library: LibraryItem[]; events: FilterEvent[] };
        setItems(body.library);
        setEvents(body.events);
      } catch {
        setError('Could not load the library.');
      }
    },
    [base],
  );

  /* Debounced so typing a company name is one request rather than one per
     keystroke. 250ms is below the point where it feels laggy. Changing the
     filter is a deliberate single act, so it does not wait. */
  useEffect(() => {
    const timer = setTimeout(() => void load(search, eventId), search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, search, eventId]);

  if (error) return <p className="auth-error">{error}</p>;

  return (
    <>
      <div className="phead">
        <div>
          <p className="eyebrow"><T k="common.appName" d="Sponsor Logos" /></p>
          <h1 className="title"><T k="library.title" d="Logo Library" /></h1>
          <p className="lede"><T k="library.lede" d="Every approved sponsor logo, reusable year over year." /></p>
        </div>
      </div>

      <div className="toolbar">
        <div className="search">
          <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            className="search-input"
            placeholder={t('library.search.placeholder', 'Search the library…')}
            aria-label={t('library.search.label', 'Search the library')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* The filter pill. Missing app-wide: `.fpill` was in the prototype's
            markup for this toolbar, the review queue and the sponsors table,
            and was defined nowhere in the stylesheet and used nowhere in the
            client. Implemented once here so the other two can pick it up;
            only this one is wired, deliberately. */}
        {events.length > 1 && (
          <label className="fpill">
            <span className="fpill-label"><T k="library.filter.heading" d="Event" /></span>
            <select
              className="fpill-select"
              value={eventId}
              aria-label={t('library.filter.label', 'Filter by event')}
              onChange={(e) => setEventId(e.target.value)}
            >
              <option value="">{t('library.filter.all', 'All events')}</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {!items ? (
        <div className="app-loading" aria-busy="true" />
      ) : items.length === 0 ? (
        <div className="empty">
          <p className="empty-title">
            {search || eventId ? 'Nothing matches' : 'The library is empty'}
          </p>
          <p className="empty-copy">
            {search || eventId
              ? 'Try part of the company name, or widen the event filter. The search also matches how a name is filed, so "VR" finds "V.R. Business Brokers".'
              : 'Artwork appears here once it has been approved on an event. Nothing is added before that, so the library only ever holds logos that are ready to use.'}
          </p>
        </div>
      ) : (
        <ul className="lgrid">
          {items.map((item) => (
            <li key={item.sponsorId} className="lcard">
              <Link className="lcard-link" to={`/c/${clientId}/library/${item.sponsorId}`}>
                {/* Checkerboard behind the artwork, since most logos are
                    transparent and a white tile hides a white-boxed one. */}
                <span className="lart">
                  {item.preview ? (
                    <img
                      src={
                        item.previewRenditions.includes('thumb')
                          ? `${base}/assets/${item.preview}/rendition/thumb`
                          : `${base}/assets/${item.preview}/preview`
                      }
                      alt=""
                    />
                  ) : (
                    <span className="lart-fallback">{initials(item.companyName)}</span>
                  )}
                </span>

                <span className="linfo">
                  <b>{item.companyName}</b>
                  <span className="lmeta">
                    {item.tierTitle && <span>{item.tierTitle}</span>}
                    <span>
                      {item.variationCount} version{item.variationCount === 1 ? '' : 's'}
                    </span>
                    {/* The event rather than a bare count. The prototype
                        showed a year; "Last used: IBBA 2026 Annual" answers
                        the same question and survives two conferences falling
                        in one calendar year. */}
                    <span>Last used: {item.latestEvent}</span>
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
